const TRY_NOW_JOURNEY_STORAGE_KEY = "hallticket.tryNowJourney.v1";
const TRY_NOW_SOURCE = "try-now";
const MAX_PERCENTILE = 100;
const MIN_PERCENTILE = 0;

export interface TryNowJourneyPayload {
  source: typeof TRY_NOW_SOURCE;
  exam: string;
  percentile: number;
  /** Vertical reservation (single select). */
  category: string;
  /** @deprecated Use `category`. Kept for older saved journeys. */
  categories?: string[];
  gender: string;
  /** @deprecated No longer collected on Try Now; kept for older saved journeys. */
  pwd?: string;
  /** Comma-separated horizontal reservations for backward compatibility. */
  quota: string;
  quotas: string[];
  createdAt: string;
}

interface SaveTryNowJourneyInput {
  exam: string;
  percentile: number | string;
  category: string;
  gender: string;
  /** @deprecated No longer collected on Try Now; kept for older saved journeys. */
  pwd?: string;
  quotas?: string[];
}

function normalizeJourneyQuotas(input: SaveTryNowJourneyInput): string[] {
  if (Array.isArray(input.quotas) && input.quotas.length > 0) {
    return input.quotas.map((item) => item.trim()).filter(Boolean);
  }
  return [];
}

function canUseLocalStorage(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function clampPercentile(value: number): number {
  return Math.min(MAX_PERCENTILE, Math.max(MIN_PERCENTILE, value));
}

function parsePercentile(value: number | string): number {
  const parsed = typeof value === "number" ? value : Number.parseFloat(value);
  if (!Number.isFinite(parsed)) {
    return MIN_PERCENTILE;
  }
  return clampPercentile(parsed);
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function parseStoredJourney(value: unknown): TryNowJourneyPayload | null {
  if (!isObject(value)) {
    return null;
  }

  const source = value.source;
  const exam = value.exam;
  const category = value.category;
  const categories = value.categories;
  const gender = value.gender;
  const pwd = typeof value.pwd === "string" ? value.pwd : undefined;
  const quota = value.quota;
  const quotas = value.quotas;
  const createdAt = value.createdAt;
  const percentile = value.percentile;

  if (source !== TRY_NOW_SOURCE || typeof exam !== "string" || typeof gender !== "string" || typeof createdAt !== "string") {
    return null;
  }

  const normalizedCategory = typeof category === "string"
    ? category.trim()
    : Array.isArray(categories)
      ? (typeof categories[0] === "string" ? categories[0].trim() : "")
      : "";

  if (!normalizedCategory) {
    return null;
  }

  const normalizedQuotas = Array.isArray(quotas)
    ? quotas.map((item) => (typeof item === "string" ? item.trim() : "")).filter(Boolean)
    : typeof quota === "string"
      ? quota.split(",").map((item) => item.trim()).filter((item) => item && item.toLowerCase() !== "none")
      : [];

  return {
    source,
    exam,
    percentile: parsePercentile(typeof percentile === "number" ? percentile : Number.parseFloat(String(percentile ?? ""))),
    category: normalizedCategory,
    ...(Array.isArray(categories) ? { categories: categories.map((item) => String(item).trim()).filter(Boolean) } : {}),
    gender,
    ...(pwd ? { pwd } : {}),
    quota: normalizedQuotas.join(", "),
    quotas: normalizedQuotas,
    createdAt,
  };
}

export function saveTryNowJourney(input: SaveTryNowJourneyInput): void {
  if (!canUseLocalStorage()) {
    return;
  }

  const category = input.category.trim();
  const quotas = normalizeJourneyQuotas(input);

  const payload: TryNowJourneyPayload = {
    source: TRY_NOW_SOURCE,
    exam: input.exam,
    percentile: parsePercentile(input.percentile),
    category,
    gender: input.gender,
    quota: quotas.join(", "),
    quotas,
    createdAt: new Date().toISOString(),
  };

  window.localStorage.setItem(TRY_NOW_JOURNEY_STORAGE_KEY, JSON.stringify(payload));
}

export function getTryNowJourney(): TryNowJourneyPayload | null {
  if (!canUseLocalStorage()) {
    return null;
  }

  const raw = window.localStorage.getItem(TRY_NOW_JOURNEY_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    return parseStoredJourney(parsed);
  } catch {
    return null;
  }
}

export function clearTryNowJourney(): void {
  if (!canUseLocalStorage()) {
    return;
  }
  window.localStorage.removeItem(TRY_NOW_JOURNEY_STORAGE_KEY);
}

export function hasPendingTryNowJourney(): boolean {
  return getTryNowJourney()?.source === TRY_NOW_SOURCE;
}
